/**
 * URL ↔ form values (de)serialization for [訪問用]領収書・請求書 list.
 * Enables bookmarkable search conditions and pagination state.
 */

import dayjs from 'dayjs';
import type { RezeptInvoiceSearchFormValues } from '@/schemas/rezeptInvoiceSearchSchema';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const intOrNull = (v: string | null): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const dateOrUndefined = (v: string | null): string | undefined => {
  if (!v || !ISO_DATE_RE.test(v)) return undefined;
  return dayjs(v).isValid() ? v : undefined;
};

const oneOfNumber = <T extends number>(
  v: string | null,
  allowed: readonly T[],
  fallback: T
): T => {
  const n = intOrNull(v);
  if (n == null) return fallback;
  return (allowed as readonly number[]).includes(n) ? (n as T) : fallback;
};

export function searchParamsToPage(sp: URLSearchParams): number {
  const n = intOrNull(sp.get('page'));
  return n && n >= 1 ? n : 1;
}

export function searchParamsToPerPage(sp: URLSearchParams, fallback: number): number {
  const n = intOrNull(sp.get('per_page'));
  return n && n >= 1 ? n : fallback;
}

/**
 * Deserialize URL params → form values.
 * Returns null when no relevant filter keys are present (bare page load).
 * Falls back to `defaults` for any field missing from the URL.
 */
export function searchParamsToFormValues(
  sp: URLSearchParams,
  defaults: Partial<RezeptInvoiceSearchFormValues> | undefined
): RezeptInvoiceSearchFormValues | null {
  const FILTER_KEYS = [
    'search_term_type',
    'search_treatement_from',
    'search_treatement_to',
    'search_treatement_one',
    'massager_id',
    'sort_radio',
  ];
  if (!FILTER_KEYS.some(k => sp.has(k))) return null;

  return {
    searchTermType: oneOfNumber(
      sp.get('search_term_type'),
      [1, 2] as const,
      (defaults?.searchTermType as 1 | 2) ?? 1
    ),
    searchTreatementFrom:
      dateOrUndefined(sp.get('search_treatement_from')) ?? defaults?.searchTreatementFrom,
    searchTreatementTo:
      dateOrUndefined(sp.get('search_treatement_to')) ?? defaults?.searchTreatementTo,
    searchTreatementOne:
      dateOrUndefined(sp.get('search_treatement_one')) ?? defaults?.searchTreatementOne,
    massagerId: intOrNull(sp.get('massager_id')) ?? 0,
    sortRadio: oneOfNumber(
      sp.get('sort_radio'),
      [1, 2, 3, 4] as const,
      (defaults?.sortRadio as 1 | 2 | 3 | 4) ?? 1
    ),
  };
}

/**
 * Serialize form values + page/perPage → URLSearchParams.
 * Empty / default-like values are omitted to keep URLs short.
 */
export function formValuesToSearchParams(
  values: RezeptInvoiceSearchFormValues,
  page: number,
  perPage: number,
  defaultPerPage: number
): URLSearchParams {
  const out = new URLSearchParams();
  out.set('search_term_type', String(values.searchTermType));
  if (values.searchTreatementFrom) out.set('search_treatement_from', values.searchTreatementFrom);
  if (values.searchTreatementTo) out.set('search_treatement_to', values.searchTreatementTo);
  if (values.searchTreatementOne) out.set('search_treatement_one', values.searchTreatementOne);
  if (values.massagerId != null && values.massagerId > 0) {
    out.set('massager_id', String(values.massagerId));
  }
  out.set('sort_radio', String(values.sortRadio));
  if (page > 1) out.set('page', String(page));
  if (perPage !== defaultPerPage) out.set('per_page', String(perPage));
  return out;
}
